import ModalEvent from '$shared/constants/ModalEvent';
import { getRandomString } from '$shared/helpers/utils';
import type { IModal, ModalEventFunction, ModalStyleOption } from '$shared/interfaces/IModal';
import { ModalStyle } from '$shared/organisms/Modal/ModalProp';
import type { Component } from 'svelte';

export type ModalOption = {
  component?: Component;
  props?: ObjectType<unknown>;
  show?: boolean;
  events?: Record<string, ModalEventFunction>;
  options?: ModalStyleOption;
  closeOnNavigate?: boolean;
};

export class Modal implements IModal {
  public readonly id: string;
  public readonly type: number | undefined;
  public readonly activeComponent: Component | undefined;

  public parentElement: HTMLElement;
  public props: ObjectType<unknown>;
  public options: ModalStyleOption = { style: ModalStyle.Popup };
  public eventCbMap: Record<string, ModalEventFunction> = {} as Record<string, ModalEventFunction>;
  public show = true;
  public closeOnNavigate = true;
  public isDismissable = true;

  constructor(type: number | undefined, component: Component | undefined) {
    this.id = getRandomString() as string;
    this.type = type;
    this.activeComponent = component;
  }

  public get isFormStyle(): boolean {
    return this.options?.style === ModalStyle.Form;
  }

  public get component(): any {
    return this.activeComponent;
  }

  open(): void {
    this.show = true;
    // Event listner is to adapt the old working flow
    document.dispatchEvent(new CustomEvent('app-modal:refresh', { detail: this.id }));
  }

  public updateProps(props: ObjectType<any>): void {
    this.props = { ...this.props, ...props };
    document.dispatchEvent(new CustomEvent('app-modal:refresh', { detail: this.id }));
  }

  public registerModalEvent(name: string, cb: ModalEventFunction): void {
    this.eventCbMap[name] = cb;
  }

  public close(): void {
    this.show = false;
    document.dispatchEvent(new CustomEvent('app-modal:close', { detail: this.id }));
    this.triggerEvenCbFunc(ModalEvent.closeCallback);
  }

  public setIsDismissable(isDismissable: boolean): this {
    this.isDismissable = isDismissable;
    return this;
  }

  public handleComponentEvent = ({ type, data = {} }: { type: string; data?: unknown }): void => {
    if (!this.show) {
      return;
    }

    if (type === ModalEvent.close) {
      this.close();
      return;
    }
    this.triggerEvenCbFunc(type, data);
  };

  private triggerEvenCbFunc(type: string, data?: unknown): void {
    const cbFunc = this.getEventCb(type);
    if (!cbFunc) return;

    cbFunc(this, data);
  }

  private getEventCb(type: string): ModalEventFunction | null {
    return this.eventCbMap[type] || null;
  }

  public static from(type: number | undefined, options?: ModalOption): Modal {
    const newModal = new Modal(type, options?.component);
    options?.props && (newModal.props = options.props);
    options?.events && (newModal.eventCbMap = options.events);
    options?.options && (newModal.options = options.options);
    typeof options?.show === 'boolean' && (newModal.show = !!options.show);
    newModal.closeOnNavigate = !!options?.closeOnNavigate;
    return newModal;
  }
}
